import debounce from 'lodash.debounce';
import { MraidStore } from 'common/stores/MraidStore';
import { DebugLog } from 'common/js/debug-log';

const RESIZE_DEBOUNCE_MS = 100;

export interface FooterRedirectResizeHandlerProps {
  adContext: AdContext;
  mraidStore: MraidStore;
  formatConfig?: { enableDynamicFooterResize?: boolean };
}

/**
 * Handles MRAID resize for footer_ad/redirect when the common Iframe notifies size via onIframeResized.
 * Triggers the MRAID command only when height changes.
 */
export class FooterRedirectResizeHandler {
  private readonly mraidStore: MraidStore;
  private lastHeight: number | null = null;
  private readonly applyResizeDebounced: (resizeProps: {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    allowOffscreen: boolean;
  }) => void;

  constructor({ mraidStore }: FooterRedirectResizeHandlerProps) {
    this.mraidStore = mraidStore;
    this.applyResizeDebounced = debounce(
      this.applyResize.bind(this),
      RESIZE_DEBOUNCE_MS
    );
  }

  private applyResize(resizeProps: {
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    allowOffscreen: boolean;
  }): void {
    this.mraidStore.setResizeProperties(resizeProps);
    this.mraidStore.resize();
  }

  /**
   * Returns a new handler instance when footer_ad + redirect, otherwise undefined.
   * Use this instead of manually checking and instantiating (e.g. in the display store).
   * adContext is typically formatOptions.context (same reference in display store).
   */
  static create(
    props: FooterRedirectResizeHandlerProps
  ): FooterRedirectResizeHandler | undefined {
    if (
      !FooterRedirectResizeHandler.shouldCreate(
        props.adContext,
        props.formatConfig
      )
    ) {
      return undefined;
    }
    return new FooterRedirectResizeHandler(props);
  }

  /**
   * Returns whether this handler should be instantiated for the given context.
   * Requires footer_ad + redirect and formatConfig.enableDynamicFooterResize === true.
   */
  static shouldCreate(
    adContext: AdContext,
    formatConfig?: { enableDynamicFooterResize?: boolean }
  ): boolean {
    return (
      adContext.adUnitType === 'footer_ad' &&
      adContext.creativeSource === 'redirect' &&
      formatConfig?.enableDynamicFooterResize === true
    );
  }

  /**
   * Call when the Iframe fires onIframeResized (e.g. from notifyResize).
   * Uses getBoundingClientRect() dimensions (width/height) for the resize.
   */
  handleResize(params: { boundingClientRect: DOMRect }): void {
    const rect = params.boundingClientRect;
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (width <= 0 || height <= 0) {
      return;
    }
    if (this.lastHeight === height) {
      return;
    }
    this.lastHeight = height;
    const resizeProps = {
      width,
      height,
      offsetX: 0,
      offsetY: 0,
      allowOffscreen: false
    };
    DebugLog.log(
      'FooterRedirectResizeHandler: setResizeProperties',
      resizeProps
    );
    this.applyResizeDebounced(resizeProps);
  }
}
